import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.jsx";
import { Input } from "@/components/ui/input.jsx";
import { Label } from "@/components/ui/label.jsx";
import { Button } from "@/components/ui/button.jsx";
import { MapPin, Image as ImageIcon, Loader2 } from 'lucide-react';

export default function StreetViewLoader({ address, onImageLoaded }) {
  const [loading, setLoading] = useState(false);
  const [localAddress, setLocalAddress] = useState(address || '');
  const [error, setError] = useState(null);

  const loadStreetView = async () => {
    if (!localAddress) {
      setError('Please enter an address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use Google Street View Static API
      // Note: Requires Google Maps API key with Street View Static API enabled
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      
      if (!apiKey) {
        throw new Error('Google Maps API key not configured. Add VITE_GOOGLE_MAPS_API_KEY to your .env file');
      }

      const encodedAddress = encodeURIComponent(localAddress);
      
      // Build Street View image URL with better zoom and angle
      const streetViewUrl = `https://maps.googleapis.com/maps/api/streetview?` +
        `size=1200x900&` +
        `location=${encodedAddress}&` +
        `fov=60&` +
        `pitch=10&` +
        `source=outdoor&` +
        `key=${apiKey}`;

      // Test if image is available
      const response = await fetch(streetViewUrl);
      if (!response.ok) {
        throw new Error('Street View image not available for this address');
      }

      // Convert to blob and create object URL
      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);

      // Call parent callback with image URL
      if (onImageLoaded) {
        onImageLoaded(imageUrl, localAddress);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-blue-600" />
          Load from Street View
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Property Address</Label>
          <Input
            value={localAddress}
            onChange={(e) => setLocalAddress(e.target.value)}
            placeholder="123 Main St, City, State ZIP"
            className="mt-1"
          />
          <p className="text-xs text-slate-500 mt-1">
            Enter the full address to load a Street View image
          </p>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <Button
          onClick={loadStreetView}
          disabled={loading || !localAddress}
          className="w-full bg-blue-600 hover:bg-blue-700"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Loading Street View...
            </>
          ) : (
            <>
              <ImageIcon className="w-4 h-4 mr-2" />
              Load Street View
            </>
          )}
        </Button>

        <div className="text-xs text-slate-500 space-y-1">
          <p><strong>Note:</strong> Requires Google Maps API key</p>
          <p>• Add VITE_GOOGLE_MAPS_API_KEY to your .env file</p>
          <p>• Enable Street View Static API in Google Cloud Console</p>
          <p>• Get your free API key at <a href="https://console.cloud.google.com" target="_blank" rel="noopener" className="text-blue-600 hover:underline">console.cloud.google.com</a></p>
        </div>
      </CardContent>
    </Card>
  );
}
